import type { UseFormSetValue } from 'react-hook-form';

export type TechsupportFormValues = {
  databases: boolean;
  discoveryServicesLogs: boolean;
  ipfChecker: boolean;
  limitDevices: boolean;
  snapshot: boolean;
  snapshotBackupDb: boolean;
  snapshotIncludeCli: boolean;
  systemLogs: boolean;
  verifySnapshot: boolean;
};

export type TechsupportRequestBody = {
  databases: boolean;
  discoveryServicesLogs: boolean;
  ipfChecker: boolean;
  limitSn: string[] | null;
  snapshot: {
    backupDb: boolean;
    id: string;
    removeCli: boolean;
  } | null;
  systemLogs: boolean;
};

export type VerifySnapshotRequestBody = {
  discoveryServicesLogs: boolean;
  limitSn: string[];
  snapshot: {
    id: string;
    removeCli: boolean;
  };
};

export type TechsupportNavigationState = { limitSns: string[] };

export const LIMIT_SN_MAX_COUNT = 10_000;

export const isTechsupportNavigationState = (
  state: unknown,
): state is TechsupportNavigationState => {
  if (typeof state !== 'object' || state === null) {
    return false;
  }

  const { limitSns } = state as Partial<TechsupportNavigationState>;
  return (
    Array.isArray(limitSns) && limitSns.every((sn) => typeof sn === 'string')
  );
};

export type PreviousSelectionValues = Pick<
  TechsupportFormValues,
  'databases' | 'snapshot'
>;

export const INITIAL_VALUES: TechsupportFormValues = {
  databases: false,
  discoveryServicesLogs: true,
  ipfChecker: true,
  limitDevices: false,
  snapshot: true,
  snapshotBackupDb: true,
  snapshotIncludeCli: true,
  systemLogs: true,
  verifySnapshot: false,
};

export const DEVICE_TABLE_ENDPOINT_CONFIG = {
  apiEndpoint: '/tables/inventory/devices',
  alternative: {
    id: 'techsupport-device-selection',
    columns: [
      'hostname',
      'siteName',
      'vendor',
      'family',
      'version',
      'platform',
      'model',
      'sn',
    ],
    title: 'Device selection table',
  },
};

export const NO_OPTION_SELECTED_MESSAGE =
  'Please select one of the options as minimum';

export const NO_DEVICE_SELECTED_MESSAGE =
  'Please select at least one device to limit the snapshot';

const assertSnapshotId = (snapshotId: string): void => {
  if (snapshotId.length === 0) {
    throw new Error('Snapshot id is required');
  }
};

const selectedFeatureLabelEntries = [
  ['ipfChecker', 'System environmental data'],
  ['systemLogs', 'System logs'],
  ['discoveryServicesLogs', 'Service logs'],
  ['databases', 'Full database dump for all snapshots'],
  ['snapshot', 'Current snapshot data'],
] as const;

type SelectionDependencyParams = {
  databases: boolean;
  previousValues: PreviousSelectionValues;
  setValue: UseFormSetValue<TechsupportFormValues>;
  snapshot: boolean;
  snapshotBackupDb: boolean;
  snapshotIncludeCli: boolean;
};

type SnapshotEnabledDependencyParams = Pick<
  SelectionDependencyParams,
  'databases' | 'setValue'
>;

type SnapshotDisabledDependencyParams = Pick<
  SelectionDependencyParams,
  'setValue' | 'snapshotBackupDb' | 'snapshotIncludeCli'
>;

type LimitDeviceDependencyParams = {
  databases: boolean;
  discoveryServicesLogs: boolean;
  limitDevices: boolean;
  setValue: UseFormSetValue<TechsupportFormValues>;
  snapshot: boolean;
  snapshotBackupDb: boolean;
};

export const hasSelectedOption = (values: TechsupportFormValues): boolean =>
  values.databases ||
  values.discoveryServicesLogs ||
  values.ipfChecker ||
  values.snapshot ||
  values.systemLogs;

export const createSelectedFeatureLabels = (
  values: TechsupportFormValues,
): string[] => [
  ...selectedFeatureLabelEntries
    .filter(([field]) => values[field])
    .map(([, label]) => label),
  ...(values.snapshot && values.snapshotIncludeCli
    ? ['CLI logs from snapshots']
    : []),
  ...(values.snapshot && values.snapshotBackupDb ? ['DB dump'] : []),
];

export const createRequestBody = (
  values: TechsupportFormValues,
  snapshotId: string | null,
  limitSn: string[] | null,
): TechsupportRequestBody => {
  if (values.snapshot) {
    assertSnapshotId(snapshotId ?? '');
  }

  return {
    databases: values.databases,
    discoveryServicesLogs: values.discoveryServicesLogs,
    ipfChecker: values.ipfChecker,
    limitSn,
    snapshot:
      values.snapshot && snapshotId
        ? {
            id: snapshotId,
            backupDb: values.snapshotBackupDb,
            removeCli: !values.snapshotIncludeCli,
          }
        : null,
    systemLogs: values.systemLogs,
  };
};

/**
 * Builds the body for POST /os/verify-snapshot.
 *
 * The verify-snapshot endpoint creates a new anonymized snapshot scoped to
 * the selected devices. Its request schema only accepts service logs and
 * CLI scrubbing — other techsupport options (databases, ipfChecker,
 * systemLogs, snapshotBackupDb) do not apply to snapshot
 * creation and are intentionally omitted. See PostOsVerifySnapshotBody.
 */
export const createVerifyRequestBody = (
  values: TechsupportFormValues,
  snapshotId: string,
  limitSn: string[],
): VerifySnapshotRequestBody => {
  assertSnapshotId(snapshotId);

  return {
    discoveryServicesLogs: values.discoveryServicesLogs,
    limitSn,
    snapshot: {
      id: snapshotId,
      removeCli: !values.snapshotIncludeCli,
    },
  };
};

const syncSnapshotEnabledDependencies = ({
  databases,
  setValue,
}: SnapshotEnabledDependencyParams): void => {
  if (databases) {
    setValue('databases', false);
  }
};

const syncSnapshotDisabledDependencies = ({
  setValue,
  snapshotBackupDb,
  snapshotIncludeCli,
}: SnapshotDisabledDependencyParams): void => {
  if (snapshotBackupDb) {
    setValue('snapshotBackupDb', false);
  }

  if (!snapshotIncludeCli) {
    setValue('snapshotIncludeCli', true);
  }
};

export const syncSelectionDependencies = ({
  databases,
  previousValues,
  setValue,
  snapshot,
  snapshotBackupDb,
  snapshotIncludeCli,
}: SelectionDependencyParams): void => {
  if (snapshot !== previousValues.snapshot) {
    if (snapshot) {
      syncSnapshotEnabledDependencies({
        databases,
        setValue,
      });
      return;
    }

    syncSnapshotDisabledDependencies({
      setValue,
      snapshotBackupDb,
      snapshotIncludeCli,
    });
    return;
  }

  if (databases !== previousValues.databases && databases) {
    setValue('snapshot', false);
  }
};

export const syncLimitDeviceDependencies = ({
  databases,
  discoveryServicesLogs,
  limitDevices,
  setValue,
  snapshot,
  snapshotBackupDb,
}: LimitDeviceDependencyParams): void => {
  if (!snapshot && limitDevices) {
    setValue('limitDevices', false);
    return;
  }

  if (!limitDevices) {
    return;
  }

  if (databases) {
    setValue('databases', false);
  }

  if (snapshotBackupDb) {
    setValue('snapshotBackupDb', false);
  }

  if (discoveryServicesLogs) {
    setValue('discoveryServicesLogs', false);
  }
};
